import { useState, useEffect, type CSSProperties } from 'react';
import { apiService, LogEntryDTO, LogsParams, type ProjectGroupsEntryDTO } from '@/services/api.service';
import { useGameGroupsStore } from '@/stores/gameGroups.store';
import styles from './LogsPage.module.scss';

/** Libellé et couleur d'un groupe de jeu journalisé, cherchés dans les groupes connus ; la clé sinon. */
function groupTag(entries: Record<string, ProjectGroupsEntryDTO>, key: string): { label: string; color?: string } {
  for (const entry of Object.values(entries)) {
    const g = entry.groups.find((x) => x.key === key);
    if (g) return { label: g.label, color: g.color };
  }
  return { label: key };
}

export function LogsPage() {
  const groupEntries = useGameGroupsStore((s) => s.entries);
  const [logs, setLogs] = useState<LogEntryDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  // Filters
  const [usernameFilter, setUsernameFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [uriFilter, setUriFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    loadLogs();
  }, [page]);

  const loadLogs = async () => {
    setLoading(true);
    setError(null);

    const params: LogsParams = {
      page,
      size: pageSize,
    };

    if (usernameFilter) params.username = usernameFilter;
    if (methodFilter) params.method = methodFilter;
    if (uriFilter) params.uri = uriFilter;
    if (statusFilter) params.statusCode = parseInt(statusFilter);

    const result = await apiService.getLogs(params);

    if (result.error) {
      setError(result.error);
      setLogs([]);
    } else if (result.data) {
      setLogs(result.data.logs);
      setTotalPages(result.data.totalPages);
      setTotal(result.data.total);
    }

    setLoading(false);
  };

  const handleSearch = () => {
    setPage(1);
    loadLogs();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getMethodClass = (method: string): string => {
    switch (method.toUpperCase()) {
      case 'GET': return styles.methodGet ?? '';
      case 'POST': return styles.methodPost ?? '';
      case 'PUT': return styles.methodPut ?? '';
      case 'PATCH': return styles.methodPatch ?? '';
      case 'DELETE': return styles.methodDelete ?? '';
      default: return '';
    }
  };

  const getStatusClass = (status: number): string => {
    if (status >= 200 && status < 300) return styles.statusSuccess ?? '';
    if (status >= 400 && status < 500) return styles.statusClientError ?? '';
    if (status >= 500) return styles.statusServerError ?? '';
    return styles.statusInfo ?? '';
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Logs d'activite</h1>
          <p className={styles.subtitle}>Consultez l'historique des requetes API</p>
        </div>
        <div className={styles.headerStats}>
          <span className={styles.totalCount}>{total.toLocaleString('fr-FR')} requetes</span>
        </div>
      </header>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <input
            type="text"
            className={styles.filterInput}
            placeholder="Utilisateur..."
            value={usernameFilter}
            onChange={(e) => setUsernameFilter(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <select
            className={styles.filterSelect}
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
          >
            <option value="">Toutes methodes</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
          <input
            type="text"
            className={styles.filterInput}
            placeholder="URI contient..."
            value={uriFilter}
            onChange={(e) => setUriFilter(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Tous statuts</option>
            <option value="200">200 OK</option>
            <option value="201">201 Created</option>
            <option value="204">204 No Content</option>
            <option value="400">400 Bad Request</option>
            <option value="401">401 Unauthorized</option>
            <option value="403">403 Forbidden</option>
            <option value="404">404 Not Found</option>
            <option value="500">500 Server Error</option>
          </select>
        </div>
        <div className={styles.filterActions}>
          <button className={styles.searchButton} onClick={handleSearch}>
            Rechercher
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Chargement des logs...</p>
          </div>
        ) : error ? (
          <div className={styles.errorState}>
            <span className={styles.errorIcon}>!</span>
            <h3>Erreur de chargement</h3>
            <p>{error}</p>
            <button className={styles.retryButton} onClick={loadLogs}>
              Reessayer
            </button>
          </div>
        ) : logs.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>i</span>
            <h3>Aucun log trouve</h3>
            <p>Aucune requete ne correspond aux filtres selectionnes.</p>
          </div>
        ) : (
          <>
            <div className={styles.logsTable}>
              <div className={styles.tableHeader}>
                <span className={styles.colTimestamp}>Date/Heure</span>
                <span className={styles.colMethod}>Methode</span>
                <span className={styles.colUri}>URI</span>
                <span className={styles.colStatus}>Statut</span>
                <span className={styles.colDuration}>Duree</span>
                <span className={styles.colUser}>Utilisateur</span>
              </div>
              <div className={styles.tableBody}>
                {logs.map((log) => (
                  <div key={log.id} className={styles.tableRow}>
                    <span className={styles.colTimestamp}>
                      {formatTimestamp(log.timestamp)}
                    </span>
                    <span className={`${styles.colMethod} ${getMethodClass(log.method)}`}>
                      {log.method}
                    </span>
                    <span className={styles.colUri} title={log.uri + (log.queryParams ? '?' + log.queryParams : '')}>
                      {log.gameGroup && (() => {
                        const tag = groupTag(groupEntries, log.gameGroup);
                        return (
                          <span
                            className={styles.groupTag}
                            title={`Groupe de jeu visé : ${tag.label}`}
                            style={tag.color ? ({ '--group-color': tag.color } as CSSProperties) : undefined}
                          >
                            {tag.label}
                          </span>
                        );
                      })()}
                      {log.uri}
                      {log.queryParams && <span className={styles.queryParams}>?{log.queryParams}</span>}
                    </span>
                    <span className={`${styles.colStatus} ${getStatusClass(log.statusCode)}`}>
                      {log.statusCode}
                    </span>
                    <span className={styles.colDuration}>
                      {log.durationMs}ms
                    </span>
                    <span className={styles.colUser}>
                      {log.username || 'anonymous'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className={styles.pagination}>
              <button
                className={styles.paginationButton}
                disabled={page <= 1}
                onClick={() => setPage(1)}
              >
                &laquo;
              </button>
              <button
                className={styles.paginationButton}
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                &lsaquo;
              </button>
              <span className={styles.paginationInfo}>
                Page {page} sur {totalPages}
              </span>
              <button
                className={styles.paginationButton}
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                &rsaquo;
              </button>
              <button
                className={styles.paginationButton}
                disabled={page >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
